import * as PIXI from "pixi.js";
import birdPng from "../assets/bird_2_cardinal.png";

class WebGLBird {
  constructor(app) {
    this.app = app;
    this.container = new PIXI.Container();
    this.birds = [];
    this.birdTexture = null;
    this.walkingFrames = {};
    this.time = 0;
    this.init();
  }

  async init() {
    try {
      this.birdTexture = await PIXI.Assets.load(birdPng);
      this.createFrames();
      this.createBird();
      this.updateBound = () => this.update();
      this.app.ticker.add(this.updateBound);
    } catch (error) {
      console.error("Failed to load bird spritesheet:", error);
    }
  }

  createFrames() {
    if (!this.birdTexture) return;

    // Calculate frame dimensions assuming 3 columns x 8 rows (24 frames total)
    const calculatedFrameWidth = Math.floor(this.birdTexture.width / 3);
    const calculatedFrameHeight = Math.floor(this.birdTexture.height / 8);

    // Use calculated dimensions
    const frameWidth = calculatedFrameWidth;
    const frameHeight = calculatedFrameHeight;

    // Create walking animation frames for all 4 directions
    // Actual spritesheet row order is: W, N, S, E (rows 0, 1, 2, 3)
    this.walkingFrames = {
      west: [], // Row 0 - flying left
      north: [], // Row 1 - flying up
      south: [], // Row 2 - flying down
      east: [], // Row 3 - flying right
    };

    const directions = ["west", "north", "south", "east"]; // Match actual spritesheet order

    // First 4 rows are flying animations, second 4 rows are walking
    // Use only the first 4 rows (0, 1, 2, 3) for flying directions
    for (let row = 0; row < 4; row++) {
      const direction = directions[row]; // west, north, south, east

      // Only use frames 0 and 2 for wing flapping animation (skip center frame)
      for (let col of [0, 2]) {
        const frame = new PIXI.Texture({
          source: this.birdTexture.source,
          frame: new PIXI.Rectangle(
            col * frameWidth,
            row * frameHeight,
            frameWidth,
            frameHeight
          ),
        });
        this.walkingFrames[direction].push(frame);
      }
    }

    // Start with south-facing frames (flying toward viewer)
    this.birdFrames = [...this.walkingFrames.south];
  }

  createBird() {
    if (!this.walkingFrames.south || this.walkingFrames.south.length === 0)
      return;

    // Start with south-facing frames
    const bird = new PIXI.AnimatedSprite(this.walkingFrames.south);

    // Configure animation for wing flapping (2 frames)
    bird.animationSpeed = 0.2; // Slightly faster wing flapping than chicken walking
    bird.play();

    // Scale bird to reasonable size
    const scale = 2.5; // Slightly larger than chicken since birds fly higher
    bird.scale.set(scale);

    // Set anchor point to center
    bird.anchor.set(0.5, 0.5);

    // Position randomly on screen, but keep 50px from bottom edge
    bird.x = Math.random() * this.app.screen.width;
    bird.y = Math.random() * (this.app.screen.height - 50);

    // Flying properties for 2D movement with angles
    bird.speed = 2; // Slightly faster than chicken
    bird.currentDirection = "south";
    bird.currentSpriteDirection = "south"; // Which sprite to show
    bird.vx = 0; // velocity x
    bird.vy = bird.speed; // velocity y (start flying down)

    // Timer for direction changes - birds change direction more frequently
    bird.directionChangeTimer = 0;
    bird.directionChangeDuration = 1500 + Math.random() * 2000; // 1.5-3.5 seconds

    this.birds.push(bird);
    this.container.addChild(bird);
  }

  update() {
    const deltaTime = 16; // ~60fps
    this.time += deltaTime;

    this.birds.forEach((bird) => {
      // Update direction change timer
      bird.directionChangeTimer += deltaTime;

      // Move bird based on current velocity
      bird.x += bird.vx;
      bird.y += bird.vy;

      // Check for screen boundaries and bounce off or wrap around
      const margin = bird.width / 2;
      let needsDirectionChange = false;

      if (bird.x < margin || bird.x > this.app.screen.width - margin) {
        // Hit left or right edge - change direction if moving horizontally
        if (Math.abs(bird.vx) > 0.5) {
          // Has significant horizontal movement
          needsDirectionChange = true;
        }
      }

      if (bird.y < margin || bird.y > this.app.screen.height - 50 - margin) {
        // Hit top or bottom edge - change direction if moving vertically
        if (Math.abs(bird.vy) > 0.5) {
          // Has significant vertical movement
          needsDirectionChange = true;
        }
      }

      // Time-based random direction change (more frequent than chicken)
      if (bird.directionChangeTimer >= bird.directionChangeDuration) {
        needsDirectionChange = true;
        bird.directionChangeTimer = 0;
        bird.directionChangeDuration = 1500 + Math.random() * 2000; // 1.5-3.5 seconds
      }

      // Change direction if needed
      if (needsDirectionChange) {
        this.changeBirdDirection(bird);
      }

      // Keep bird within screen bounds, with 50px minimum from bottom
      bird.x = Math.max(
        margin,
        Math.min(this.app.screen.width - margin, bird.x)
      );
      bird.y = Math.max(
        margin,
        Math.min(this.app.screen.height - 50 - margin, bird.y)
      );
    });
  }

  changeBirdDirection(bird) {
    // 8-direction movement: N, NE, E, SE, S, SW, W, NW
    const angles = [
      0, // North
      45, // Northeast
      90, // East
      135, // Southeast
      180, // South
      225, // Southwest
      270, // West
      315, // Northwest
    ];

    // Pick a random angle
    const randomAngle = angles[Math.floor(Math.random() * angles.length)];
    const radians = (randomAngle * Math.PI) / 180;

    // Calculate velocity components
    bird.vx = Math.sin(radians) * bird.speed;
    bird.vy = -Math.cos(radians) * bird.speed; // Negative because Y increases downward

    // Determine which sprite direction to use based on angle
    let spriteDirection;
    if (randomAngle >= 315 || randomAngle < 45) {
      spriteDirection = "north";
    } else if (randomAngle >= 45 && randomAngle < 135) {
      spriteDirection = "east";
    } else if (randomAngle >= 135 && randomAngle < 225) {
      spriteDirection = "south";
    } else {
      spriteDirection = "west";
    }

    bird.currentDirection = randomAngle; // Store actual angle
    bird.currentSpriteDirection = spriteDirection; // Store sprite to use

    // Update sprite textures to match closest direction
    const newFrames = this.walkingFrames[spriteDirection];
    if (newFrames && newFrames.length > 0) {
      bird.textures = newFrames;
      bird.gotoAndPlay(0); // Restart animation with new frames
    }
  }

  destroy() {
    try {
      if (this.updateBound && this.app && this.app.ticker) {
        this.app.ticker.remove(this.updateBound);
        this.updateBound = null;
      }

      this.birds = [];

      if (this.container) {
        this.container.destroy({ children: true, texture: false });
        this.container = null;
      }
    } catch (e) {
      console.warn("Error destroying WebGLBird:", e);
    }
  }
}

export default WebGLBird;
